// T-263 e T-264 — o que cada um dos seis perfis enxerga na barra lateral.
//
// O mapa é a transcrição da reunião de 24/09, tela a tela, e está escrito assim, como LISTA DE
// NOMES, porque foi assim que a Tamara ditou. Adivinhar por categoria ("vê configurações",
// "vê relatórios") daria menos linhas e erraria nos casos que ela citou nominalmente: o Analista
// de CS vê oito telas de configuração e não vê a nona (atribuição de agentes), e a Curadoria vê
// duas, não a seção inteira.
//
// Os nomes são os mesmos do menu em Sidebar.vue. Item que não estiver na lista de um perfil não
// aparece para ele.
//
// ⚠️ ISTO DECIDE O QUE APARECE, NÃO O QUE É PERMITIDO. Curadoria de IA e Analista de CS são
// administradores de verdade para o servidor: a barra lateral esconde, a API não. Ver
// Fandata::Perfis.

// Quem vê tudo, com UMA exceção para o Administrador: ver `SO_DO_DESENVOLVEDOR` abaixo.
export const PERFIS_TOTAIS = ['admin', 'desenvolvedor'];

// T-265 (01/10) — o que é "coisa de desenvolvedor", e só ele vê.
//
// O recorte veio do Douglas, nominalmente, e é o mesmo critério das duas telas: elas configuram
// a INSTALAÇÃO, não a operação. Configurações da conta mexe em domínio, idioma e exclusão da
// conta; Integrações liga e desliga serviço externo. Errar ali não atrapalha um atendimento,
// quebra o ambiente inteiro, e quem responde por isso é quem mantém a plataforma.
//
// ⚠️ ISTO ESCONDE, NÃO BLOQUEIA, como todo este arquivo. O Administrador continua sendo
// administrador para o servidor: se digitar a URL, a tela abre. O que muda é a entrada deixar de
// existir no menu dele, que é o que evita o clique por engano. Bloquear de verdade exigiria
// mexer na autorização do upstream, que serve o produto inteiro.
const SO_DO_DESENVOLVEDOR = ['Settings Account Settings', 'Settings Integrations'];

// Seções de primeiro nível.
const SECOES = {
  agente: ['Conversation'],
  backoffice: ['Conversation', 'Contacts'],
  curadoria_ia: ['Inbox', 'Conversation', 'Reports', 'Settings'],
  analista_cs: ['Inbox', 'Conversation', 'Contacts', 'Reports', 'Settings'],
};

// Filhos, por seção. `undefined` quer dizer "todos os filhos daquela seção".
const FILHOS = {
  agente: {
    Conversation: ['Mentions', 'Participating'],
  },
  backoffice: {
    // "em conversas vê todas, menções, minhas conversas e pastas. Não vê canais."
    Conversation: ['All', 'Mentions', 'Participating', 'Folders'],
  },
  curadoria_ia: {
    // "vê caixa de entrada, conversas, menções e minhas conversas. Não vê pastas nem canais."
    Conversation: ['All', 'Mentions', 'Participating'],
    // "em relatórios vê só os de IA". Farol entra junto do Impacto porque é o relatório que
    // julga a classificação da IA, que é o trabalho da curadoria.
    Reports: ['Report IA Impact', 'Report Farol'],
    // "em configurações vê base de conhecimento e Bira" (24/09), mais Automação, Macros,
    // Respostas Prontas e Modelos de e-mail, acrescentados pela Tamara em 02/10. A lógica do
    // acréscimo é consistente: tudo o que a curadoria faz é AJUSTAR O QUE A IA RESPONDE, e a
    // resposta pronta e a macro são tão conteúdo quanto a base.
    Settings: [
      'Settings Bira Base',
      'Settings Capitao',
      'Settings Automation',
      'Settings Macros',
      'Settings Canned Responses',
      'Settings Modelos de e-mail',
    ],
  },
  analista_cs: {
    // "conversas (todas, menções, minhas), pastas, canais".
    Conversation: ['All', 'Mentions', 'Participating', 'Folders', 'Channels'],
    // "auditoria, agentes, respostas prontas, times, contatos duplicados, parceiros, modelos de
    // e-mail e configuração de tabulação. Não vê a tela de atribuição de agentes."
    Settings: [
      'Settings Bira Auditoria',
      'Settings Agents',
      'Settings Canned Responses',
      'Settings Teams',
      'Settings Duplicados',
      'Settings Parceiros',
      'Settings Modelos de e-mail',
      'Settings Labels',
    ],
  },
};

// Os seis, na ordem do mais estreito para o mais amplo, com o papel nativo que cada um exige.
// Espelha Fandata::Perfis::DEFINICOES: a lista existe dos dois lados porque um lado valida e o
// outro desenha, e não vale a pena um endpoint só para transportar seis linhas que não mudam.
// Mexeu aqui, mexa lá.
export const PERFIS_FANDATA = [
  { chave: 'agente', papel: 'agent' },
  { chave: 'backoffice', papel: 'agent' },
  { chave: 'curadoria_ia', papel: 'administrator' },
  { chave: 'analista_cs', papel: 'administrator' },
  { chave: 'admin', papel: 'administrator' },
  { chave: 'desenvolvedor', papel: 'administrator' },
];

export const papelDoPerfil = chave =>
  PERFIS_FANDATA.find(p => p.chave === chave)?.papel || 'agent';

export const vePerfilTudo = perfil => PERFIS_TOTAIS.includes(perfil);

// Saída de emergência (02/10): quem tem PAPEL NATIVO de administrador nunca perde Configurações
// → Agentes, qualquer que seja o perfil gravado ou pré-visualizado.
//
// Sem isso a escolha é de mão única. Administrador que se der o perfil Agente perde justamente a
// tela que desfaz a escolha, e precisaria de outra pessoa, ou do console, para voltar. É o mesmo
// motivo de a pré-visualização existir: conferir a tela do outro não pode custar o próprio
// acesso.
//
// ⚠️ NÃO VALE DURANTE A PRÉ-VISUALIZAÇÃO (03/10). Quem chama passa `ehAdminNativo = false` nesse
// caso, e a razão é que lá a saída já existe e é melhor: o botão "Voltar" na faixa do topo, que
// não depende de item de menu nenhum. A versão de 02/10 mantinha a exceção ligada também na
// prévia, e a Tamara relatou no mesmo dia, ao validar: "vi que o agente tem acesso às
// configurações da plataforma". A prévia tem de ser fiel, senão não serve para conferir.
const SAIDA_DE_EMERGENCIA = { secao: 'Settings', filho: 'Settings Agents' };

// A seção de primeiro nível aparece para este perfil?
export const perfilVeSecao = (perfil, nome, ehAdminNativo = false) => {
  if (ehAdminNativo && nome === SAIDA_DE_EMERGENCIA.secao) return true;

  return (
    vePerfilTudo(perfil) || (SECOES[perfil] || SECOES.agente).includes(nome)
  );
};

// O filho aparece para este perfil? Seção sem lista de filhos mostra todos.
export const perfilVeFilho = (perfil, secao, nome, ehAdminNativo = false) => {
  if (ehAdminNativo && nome === SAIDA_DE_EMERGENCIA.filho) return true;
  // A exceção do T-265 vem antes do "vê tudo": o Administrador vê tudo, menos estas duas.
  if (perfil === 'admin' && SO_DO_DESENVOLVEDOR.includes(nome)) return false;
  if (vePerfilTudo(perfil)) return true;
  const permitidos = (FILHOS[perfil] || FILHOS.agente)[secao];
  return permitidos ? permitidos.includes(nome) : true;
};
